import type { AnimationState, TransformState, TransitionConfig } from '../types'
import { DEFAULT_TRANSFORM, DEFAULT_TRANSITION } from '../constants'

type Framework = 'react' | 'vue' | 'vanilla'

function getNonDefaultTransform(transform: TransformState): Partial<TransformState> {
  const diff: Partial<TransformState> = {}
  for (const key of Object.keys(DEFAULT_TRANSFORM) as (keyof TransformState)[]) {
    if (transform[key] !== DEFAULT_TRANSFORM[key]) {
      diff[key] = transform[key]
    }
  }
  return diff
}

function getTransitionOptions(transition: TransitionConfig): Record<string, any> {
  switch (transition.type) {
    case 'spring': {
      const opts: Record<string, any> = { type: '"spring"' }
      const d = DEFAULT_TRANSITION.spring
      if (transition.spring.stiffness !== d.stiffness) opts.stiffness = transition.spring.stiffness
      if (transition.spring.damping !== d.damping) opts.damping = transition.spring.damping
      if (transition.spring.mass !== d.mass) opts.mass = transition.spring.mass
      if (transition.spring.bounce !== d.bounce) opts.bounce = transition.spring.bounce
      return opts
    }
    case 'tween': {
      const opts: Record<string, any> = {}
      opts.duration = transition.tween.duration
      opts.ease = `"${transition.tween.ease}"`
      return opts
    }
    case 'inertia': {
      const opts: Record<string, any> = { type: '"inertia"' }
      opts.velocity = transition.inertia.velocity
      opts.power = transition.inertia.power
      opts.min = transition.inertia.min
      opts.max = transition.inertia.max
      return opts
    }
  }
}

function formatObjectLiteral(obj: Record<string, any>, indent: number = 2): string {
  const entries = Object.entries(obj)
  if (entries.length === 0) return '{}'
  const pad = ' '.repeat(indent)
  const lines = entries.map(([k, v]) => `${pad}${k}: ${v},`)
  return `{\n${lines.join('\n')}\n${' '.repeat(indent - 2)}}`
}

export function generateReactCode(state: AnimationState): string {
  const transform = getNonDefaultTransform(state.transform)
  const transition = getTransitionOptions(state.transition)

  const animateObj = formatObjectLiteral(transform, 4)
  const transitionObj = formatObjectLiteral(transition, 4)

  return `import { motion } from "motion/react"

function AnimatedBox() {
  return (
    <motion.div
      animate={${animateObj}}
      transition={${transitionObj}}
      className="w-24 h-24 bg-blue-500 rounded-lg"
    />
  )
}`
}

export function generateVueCode(state: AnimationState): string {
  const transform = getNonDefaultTransform(state.transform)
  const transition = getTransitionOptions(state.transition)

  const animateObj = formatObjectLiteral(transform, 4)
  const transitionObj = formatObjectLiteral(transition, 4)

  return `<script setup>
import { Motion } from "motion-v"
</script>

<template>
  <Motion
    :animate="${animateObj}"
    :transition="${transitionObj}"
    class="w-24 h-24 bg-blue-500 rounded-lg"
  />
</template>`
}

export function generateVanillaCode(state: AnimationState): string {
  const transform = getNonDefaultTransform(state.transform)
  const transition = getTransitionOptions(state.transition)

  const keyframesObj = formatObjectLiteral(transform, 4)
  const optionsObj = formatObjectLiteral(transition, 4)

  return `import { animate } from "motion"

const el = document.querySelector(".box")

animate(
  el,
  ${keyframesObj},
  ${optionsObj}
)`
}

export function generateCode(state: AnimationState, framework: Framework): string {
  switch (framework) {
    case 'react':
      return generateReactCode(state)
    case 'vue':
      return generateVueCode(state)
    case 'vanilla':
      return generateVanillaCode(state)
  }
}
